import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { COLORS } from "../../header/header.styled";

/* --------- Shared primitives --------- */

export const fontStack = css`
  font-family:
    Inter,
    system-ui,
    -apple-system,
    Segoe UI,
    Roboto,
    "Helvetica Neue",
    Arial,
    "Noto Sans",
    sans-serif;
`;

export const Panel = styled.section`
  ${fontStack}
  margin: 0 16px 16px;
  padding: 16px 16px 0;
  border-radius: 12px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  box-shadow: 0 1px 0 ${COLORS.neutralOverlay} inset;
  overflow: auto;
  height: fit-content;
`;

export const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const FormGrid = styled.form`
  ${fontStack}
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: fit-content;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));

  /* Normalize children */
  & > * {
    margin: 0;
    padding: 0;
  }
`;

export const RowBetween = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: start;
  gap: 8px;
`;

export const Stack = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap = 4 }) => `${$gap}px`};
`;

/* --------- Typography --------- */

export const Title = styled.h3`
  ${fontStack}
  margin: 0;
  font-weight: 600;
  font-size: 16px;
  line-height: 140%;
  color: ${COLORS.textPrimary};
`;

export const SubTitle = styled.h4`
  ${fontStack}
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  color: ${COLORS.textPrimary};
  line-height: 1.4;
`;

export const MutedSubTitle = styled.h4`
  ${fontStack}
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  color: ${COLORS.textSecondary};
  line-height: 1.4;
`;

export const DeviceName = styled.h5`
  ${fontStack}
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  color: ${COLORS.textPrimary};
`;

export const HelperText = styled.p`
  ${fontStack}
  margin: 0;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.4;
  color: #B94C37;
`;

export const PasskeyText = styled.p`
  ${fontStack}
  margin: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
`;

export const DateText = styled.p`
  ${fontStack}
  margin: 0;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
`;

export const MutedText = styled.p`
  ${fontStack}
  margin: 0;
  font-weight: 400;
  font-size: 13px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
`;

/* --------- Buttons / Actions --------- */

export const ActionText = styled.button`
  ${fontStack}
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-weight: 600;
  line-height: 140%;
  font-size: 14px;
  color: ${COLORS.accent};
  text-decoration: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DeleteAccountButton = styled.button`
  ${fontStack}
  width: 142px;
  height: 44px;
  padding: 12px 16px;
  gap: 8px;
  border-radius: 12px;
  background-color: #d65f46;
  color: #ffffff;
  font-weight: 600;
  font-size: 15px;
  line-height: 140%;
  text-align: center;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;

  &:active {
    transform: translateY(1px);
  }
`;

export const ConfirmBtn = styled.button`
  ${fontStack}
  height: 44px;
  background: ${COLORS.btnRest};
  color: ${COLORS.onPrimary};
  border: 1px solid ${COLORS.outline};
  padding: 12px 16px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${COLORS.btnHover};
  }
`;

export const CancelBtn = styled.button`
  ${fontStack}
  background: transparent;
  color: ${COLORS.textSecondary};
  border: none;
  padding: 12px 16px;
  height: 44px;
  border-radius: 12px;
  cursor: pointer;

  &:hover {
    background: ${COLORS.neutralOverlay};
  }
`;

export const CancelTextButton = styled.button`
  ${fontStack}
  border: 0;
  background: none;
  color: ${COLORS.textMuted};
  font-weight: 600;
  font-size: 15px;
  height: 44px;
  padding: 0 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* --------- Channel card + consent --------- */

export const ChannelCard = styled.div`
  width: 100%;
  height: 44px;
  min-height: 44px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  opacity: 1;
  cursor: pointer;

  &:hover {
    background: ${COLORS.cardElevated ?? COLORS.neutralOverlay};
  }
`;

export const ChannelLabel = styled.label`
  ${fontStack}
  font-size: 14px;
  line-height: 1;
  color: ${COLORS.textPrimary};
  -webkit-user-select: none;
  user-select: none;
  cursor: pointer;
`;

export const ChannelMeta = styled.span`
  ${fontStack}
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: ${COLORS.textMuted};
  font-style: italic;
`;

export const ChannelCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${COLORS.accent};
  cursor: pointer;
`;

export const ConsentBox = styled.div`
  width: 100%;
  background: ${COLORS.general};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ConsentText = styled.p`
  ${fontStack}
  margin: 0;
  color: ${COLORS.textSecondary};
  font-size: 13px;
  line-height: 1.4;
`;

export const InlineLink = styled.a`
  color: ${COLORS.link};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/* --------- Alert dialog (Radix) --------- */

import * as AlertDialog from "@radix-ui/react-alert-dialog";

export const AlertOverlay = styled(AlertDialog.Overlay)`
  position: fixed;
  inset: 0;
  @media (min-width: 768px) { left: var(--sidebar-width, 0px); }
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
`;

/** Host element for <AlertDialog.Content asChild> */
export const AlertContentShell = styled.div`
  position: fixed;
  top: calc(50% - var(--keyboard-height, 0px) / 2);
  left: 50%;
  transform: translate(-50%, -50%);
  @media (min-width: 768px) { left: calc(50% + var(--sidebar-width, 0px) / 2); }
  width: calc(100% - 64px);
  max-width: 310px;
  max-height: calc(100vh - var(--keyboard-height, 0px) - env(safe-area-inset-top, 0px) - 40px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  border-radius: 12px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.24);
  padding: 20px 20px 16px 20px;
  z-index: 10000;
`;
